import SVG from "react-inlinesvg";
import { Box, Button, Flex, Link, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { throttle } from "lodash";
import useIdentity from "@/features/ui/hooks/use-identity";
import { signInUtm } from "@/features/landing/constants";
import { getDefaultPageByIdentity } from "@/pages";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import CustomIcon from "../../../../public/icons-v1_1";

interface Props {
  isSignedIn: boolean;
  // "서비스로 이동" 목적지 override — 패스픽 랜딩처럼 회사 타입이 아니라
  // 패스픽 가입 여부로 목적지가 갈리는 화면에서 쓴다. 없으면 기존 동작 그대로.
  serviceHref?: string;
  // 배경이 흰 페이지(패스픽 랜딩)에서는 최상단에서도 로고·언어 버튼이 흰색이면 보이지 않는다.
  // 켜면 스크롤 여부와 무관하게 항상 어두운 색으로 고정한다.
  isAlwaysDark?: boolean;
  // 흰 배경 페이지에서는 흰색 20% 보더가 보이지 않아 헤더 경계가 사라진다 — 대신 얇은 그림자를 준다.
  hasBottomShadow?: boolean;
}

const options: AppSelectOption<string>[] = [
  {
    value: "en",
    name: "English",
  },
  {
    value: "ko",
    name: "한국어",
  },
  // {
  //   value: "it",
  //   name: "Italiano",
  // },
];

const LandingPageHeader = ({
  isSignedIn,
  serviceHref,
  isAlwaysDark = false,
  hasBottomShadow = false,
}: Props) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const identity = useIdentity();
  const { tSentence } = useI18nHelper();

  // 배경은 스크롤을 따라가지만 글자·로고 색은 페이지가 강제할 수 있다.
  const isDarkText = isAlwaysDark || isScrolled;

  useEffect(() => {
    const scrollHandler = () => setIsScrolled(window.scrollY !== 0);
    const throttled = throttle(scrollHandler, 100);

    document.addEventListener("scroll", throttled);
    return () => document.removeEventListener("scroll", throttled);
  }, []);

  return (
    <Flex
      justifyContent={"center"}
      padding={{
        base: "12px 16px",
        lg: "16px 24px",
        xl: "16px 320px",
      }}
      borderBottom={
        hasBottomShadow
          ? "1px solid transparent"
          : "1px solid rgba(255, 255, 255, 0.20)"
      }
      boxShadow={hasBottomShadow ? "0 0.5px 3px rgba(0, 0, 0, 0.12)" : undefined}
      position={"fixed"}
      top={0}
      width={"100%"}
      zIndex={10000}
      background={isScrolled ? "#FFFFFF" : "transparent"}
      minWidth={"320px"}
    >
      <Flex
        flex={1}
        maxWidth={"1318px"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Link
          aria-label={"Pathrade logo"}
          href={"/"}
          display={{
            base: "none",
            md: "block",
          }}
          width={{
            md: "136px",
            lg: "204px",
          }}
          height={{
            md: "24px",
            lg: "36px",
          }}
          color={isDarkText ? "#444444" : "#FFFFFF"}
        >
          <CustomIcon
            src={"logo"}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </Link>
        <Link
          aria-label={"Pathrade mobile logo"}
          href={"/"}
          display={{
            base: "block",
            md: "none",
          }}
          color={isDarkText ? "#444444" : "#FFFFFF"}
        >
          <CustomIcon
            src={"logo-mobile"}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </Link>
        <Flex as={"nav"} aria-label={"Auth navigation"} gap={"12px"}>
          <LanguageToggleBtn isDarkText={isDarkText} />
          <Link
            padding={{
              base: "8px 12px",
              lg: "12px 24px",
            }}
            fontSize={{
              base: "14px",
              lg: "16px",
            }}
            lineHeight={{
              base: "20px",
              lg: "24px",
            }}
            style={{
              fontWeight: "bold",
              background: "#1272EF",
              color: "#FFFFFF",
              height: "auto",
              borderRadius: "8px",
              whiteSpace: "nowrap",
            }}
            height={"auto"}
            className={"interaction-layer-strong"}
            _hover={{
              textDecoration: "none",
            }}
            href={
              isSignedIn
                ? serviceHref ?? getDefaultPageByIdentity(identity)
                : signInUtm
            }
          >
            {isSignedIn
              ? tSentence("landing:enter_service")
              : tSentence("landing:sign_in")}
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default LandingPageHeader;

interface LanguageToggleBtnProps {
  isDarkText: boolean;
}

const LanguageToggleBtn = ({ isDarkText }: LanguageToggleBtnProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useI18nHelper();
  const isKor = i18n.language === "ko";

  return (
    <Button
      padding={{
        base: "8px 12px",
        lg: "12px 24px",
      }}
      fontSize={{
        base: "14px",
        lg: "16px",
      }}
      lineHeight={{
        base: "20px",
        lg: "24px",
      }}
      style={{
        fontWeight: "bold",
        color: isDarkText ? "#555555" : "#FFFFFF",
        height: "auto",
        borderRadius: "8px",
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
      variant={"ghost"}
      _hover={{
        background: "none",
      }}
      className={"interaction-layer-strong"}
      onClick={async () => {
        const y = window.scrollY; // 또는 document.documentElement.scrollTop
        sessionStorage.setItem("__restore_scroll_y__", String(y));

        const nextLang = isKor ? "en" : "ko";
        if (nextLang) {
          const parameters = location.pathname
            .split("/")
            .map((p) => p.trim())
            .filter((p) => p !== "");

          const candidates = options
            .map((option) => option.value)
            .filter((value) => value !== "en");

          if (candidates.includes(parameters[0])) {
            parameters.shift();
          }

          await i18n.changeLanguage(nextLang);

          if (nextLang !== "en") {
            parameters.unshift(nextLang);
          }

          const next = "/" + parameters.join("/") + location.search;

          navigate(next, { replace: true });
        }
      }}
    >
      {isKor ? "EN" : "KO"}
    </Button>
  );
};
